/**
 * StrategyLiftDialog
 *
 * Two-step lift: step 1 captures the onboarding version coordinates, step 2
 * reviews the dry-run plan (blocks to create, loose-screen placement with
 * suggested blocks, variable diff, ignored foreign tags) and sends the
 * collected resolutions with the real lift.
 */
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Alert,
  Box,
  Chip,
  FormControlLabel,
  Stack,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import {
  BaseDialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  StyledSelectDropdown,
  StyledTextField,
} from '@modules/ui-components';
import { OnboardingService } from '@modules/firebase-core/services/OnboardingService';
import type { Onboarding, OnboardingVersion } from '@modules/firebase-core/converters/onboardingConverter';
import type {
  CliStrategyForeignTagEntry,
  CliStrategyLiftRequest,
  CliStrategyLooseScreenEntry,
  CliStrategySyncResolutions,
  CliStrategyVariableDiffEntry,
} from '@modules/shared-types';
import {
  StrategySyncPlanView,
  type StrategySyncBlockOption,
  type StrategySyncPlanData,
} from './StrategySyncPlanView';

export interface StrategyLiftPayload extends CliStrategyLiftRequest {
  funnelId: string;
  versionId: string;
}

export interface StrategyLiftPreviewResult {
  blockCount?: number;
  screenCount?: number;
  blocks?: Array<Record<string, unknown>>;
  looseScreens?: CliStrategyLooseScreenEntry[];
  foreignTagIgnored?: CliStrategyForeignTagEntry[];
  variableDiff?: CliStrategyVariableDiffEntry[];
  warnings?: string[];
  error?: string;
}

export interface StrategyLiftDialogProps {
  open: boolean;
  projectId: string;
  defaultStrategyId: string;
  defaultName?: string;
  /**
   * The onboarding this strategy is associated with (its own compiled/lifted
   * funnel). When set, the dialog pre-selects it — the round-trip source is the
   * common case and is always a valid, liftable version.
   */
  defaultFunnelId?: string;
  loading?: boolean;
  onClose: () => void;
  /** Dry-run lift returning the plan for the review step. */
  onPreview: (payload: StrategyLiftPayload) => Promise<StrategyLiftPreviewResult>;
  onLift: (payload: StrategyLiftPayload) => Promise<void>;
}

const HEAD_CELL_SX = { fontWeight: 700, bgcolor: 'action.hover' } as const;

export const StrategyLiftDialog: React.FC<StrategyLiftDialogProps> = ({
  open,
  projectId,
  defaultStrategyId,
  defaultName,
  defaultFunnelId,
  loading = false,
  onClose,
  onPreview,
  onLift,
}) => {
  const onboardingServiceRef = useRef(new OnboardingService());

  const [onboardings, setOnboardings] = useState<Onboarding[]>([]);
  const [versions, setVersions] = useState<OnboardingVersion[]>([]);
  const [funnelId, setFunnelId] = useState('');
  const [versionId, setVersionId] = useState('');
  const [targetStrategyId, setTargetStrategyId] = useState(defaultStrategyId);
  const [name, setName] = useState(defaultName || '');
  const [replaceExisting, setReplaceExisting] = useState(true);
  const [onboardingsLoading, setOnboardingsLoading] = useState(false);
  const [versionsLoading, setVersionsLoading] = useState(false);
  const [loadError, setLoadError] = useState<string | null>(null);

  const [step, setStep] = useState<'form' | 'plan'>('form');
  const [previewLoading, setPreviewLoading] = useState(false);
  const [preview, setPreview] = useState<StrategyLiftPreviewResult | null>(null);
  const [previewError, setPreviewError] = useState<string | null>(null);
  const [resolutions, setResolutions] = useState<CliStrategySyncResolutions>({});

  useEffect(() => {
    if (!open) return;

    // Pre-select the strategy's own associated onboarding (round-trip source);
    // the version effect then auto-picks its current version.
    setFunnelId(defaultFunnelId || '');
    setVersionId('');
    setTargetStrategyId(defaultStrategyId);
    setName(defaultName || '');
    setReplaceExisting(true);
    setVersions([]);
    setLoadError(null);
    setStep('form');
    setPreview(null);
    setPreviewError(null);
    setResolutions({});
  }, [open, defaultStrategyId, defaultName, defaultFunnelId]);

  useEffect(() => {
    if (!open) return;

    let cancelled = false;
    setOnboardingsLoading(true);
    setLoadError(null);

    onboardingServiceRef.current.listOnboardings(projectId)
      .then((items) => {
        if (cancelled) return;
        setOnboardings(items);
      })
      .catch((error) => {
        if (cancelled) return;
        setOnboardings([]);
        setLoadError(error instanceof Error ? error.message : 'Failed to load onboardings');
      })
      .finally(() => {
        if (!cancelled) {
          setOnboardingsLoading(false);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [open, projectId]);

  useEffect(() => {
    if (!open || !funnelId) {
      setVersions([]);
      setVersionId('');
      return;
    }

    let cancelled = false;
    setVersionsLoading(true);
    setLoadError(null);

    onboardingServiceRef.current.listVersions(projectId, funnelId)
      .then((items) => {
        if (cancelled) return;

        setVersions(items);
        const selectedOnboarding = onboardings.find((item) => item.id === funnelId);
        const currentVersionId = selectedOnboarding?.latestVersionId;
        const defaultVersionId = currentVersionId && items.some((item) => item.id === currentVersionId)
          ? currentVersionId
          : items[0]?.id || '';

        setVersionId(defaultVersionId);
      })
      .catch((error) => {
        if (cancelled) return;
        setVersions([]);
        setVersionId('');
        setLoadError(error instanceof Error ? error.message : 'Failed to load onboarding versions');
      })
      .finally(() => {
        if (!cancelled) {
          setVersionsLoading(false);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [open, projectId, funnelId, onboardings]);

  const selectedOnboarding = useMemo(
    () => onboardings.find((item) => item.id === funnelId),
    [funnelId, onboardings],
  );

  const onboardingOptions = useMemo(() => onboardings.map((item) => ({
    value: item.id,
    label: `${item.name || 'Untitled onboarding'} - ${item.id}`,
  })), [onboardings]);

  const versionOptions = useMemo(() => versions.map((item) => {
    const isCurrent = selectedOnboarding?.latestVersionId === item.id
      || (!selectedOnboarding?.latestVersionId && versions[0]?.id === item.id);
    return {
      value: item.id,
      label: `${isCurrent ? 'Current version: ' : ''}${item.name || 'Untitled version'} - ${item.id}`,
    };
  }), [selectedOnboarding?.latestVersionId, versions]);

  const busy = loading || previewLoading;
  const canSubmit = funnelId.trim().length > 0
    && versionId.trim().length > 0
    && !busy
    && !onboardingsLoading
    && !versionsLoading;

  const handleClose = useCallback(() => {
    if (busy) return;
    onClose();
  }, [busy, onClose]);

  const buildPayload = useCallback((): StrategyLiftPayload => {
    const trimmedTargetStrategyId = targetStrategyId.trim();
    const trimmedName = name.trim();

    return {
      funnelId: funnelId.trim(),
      versionId: versionId.trim(),
      targetStrategyId: trimmedTargetStrategyId || undefined,
      name: trimmedName || undefined,
      replaceExisting,
    };
  }, [funnelId, versionId, targetStrategyId, name, replaceExisting]);

  const handlePreview = useCallback(async () => {
    if (!canSubmit) return;

    setPreviewLoading(true);
    setPreviewError(null);
    try {
      const result = await onPreview(buildPayload());
      if (result.error) {
        setPreviewError(result.error);
        return;
      }
      setPreview(result);
      setResolutions({});
      setStep('plan');
    } catch (err) {
      setPreviewError((err as Error)?.message || 'Lift preview failed');
    } finally {
      setPreviewLoading(false);
    }
  }, [buildPayload, canSubmit, onPreview]);

  const handleLift = useCallback(async () => {
    if (busy) return;
    await onLift({ ...buildPayload(), resolutions });
  }, [busy, buildPayload, onLift, resolutions]);

  const previewBlocks = useMemo(() => (preview?.blocks ?? []).map((block) => ({
    entityId: String((block as any).entityId ?? ''),
    name: typeof (block as any).name === 'string' ? (block as any).name as string : undefined,
    blockType: typeof (block as any).blockType === 'string' ? (block as any).blockType as string : undefined,
    screenCount: Array.isArray((block as any).screens) ? ((block as any).screens as unknown[]).length : 0,
  })), [preview]);

  const availableBlocks: StrategySyncBlockOption[] = useMemo(() => previewBlocks
    .filter((block) => block.entityId && block.entityId !== '_untagged')
    .map((block) => ({ blockEntityId: block.entityId, name: block.name })), [previewBlocks]);

  const planData: StrategySyncPlanData | null = useMemo(() => (preview
    ? {
      warnings: preview.warnings,
      variableDiff: preview.variableDiff,
      looseScreens: preview.looseScreens,
      foreignTagIgnored: preview.foreignTagIgnored,
    }
    : null), [preview]);

  const primaryAction = useMemo(() => (step === 'form'
    ? {
      label: previewLoading ? 'Preparing plan...' : 'Preview plan',
      onClick: handlePreview,
      disabled: !canSubmit,
      loading: previewLoading,
      'data-testid': 'strategy-lift-submit',
    }
    : {
      label: loading ? 'Lifting...' : `Lift ${preview?.screenCount ?? 0} screens`,
      onClick: handleLift,
      disabled: busy,
      loading,
      'data-testid': 'strategy-lift-confirm',
    }), [busy, canSubmit, handleLift, handlePreview, loading, preview?.screenCount, previewLoading, step]);

  const secondaryAction = useMemo(() => ({
    label: 'Cancel',
    onClick: handleClose,
    disabled: busy,
  }), [handleClose, busy]);

  const additionalActions = useMemo(() => (step === 'plan'
    ? [{
      label: 'Back',
      onClick: () => setStep('form'),
      disabled: busy,
    }]
    : undefined), [busy, step]);

  return (
    <BaseDialog
      open={open}
      onClose={handleClose}
      maxWidth={step === 'plan' ? 'md' : 'sm'}
      fullWidth
      mode="modal"
      data-testid="strategy-lift-dialog"
      aria-labelledby="strategy-lift-dialog-title"
    >
      <DialogHeader
        title="Lift Onboarding to Strategy"
        showCloseButton={false}
      />

      <DialogContent>
        {step === 'form' ? (
          <Stack spacing={2}>
            {loadError && (
              <Alert severity="error">
                {loadError}
              </Alert>
            )}

            {previewError && (
              <Alert severity="error" data-testid="strategy-sync-error">
                {previewError}
              </Alert>
            )}

            <StyledSelectDropdown
              label="Onboarding"
              value={funnelId}
              onChange={(value) => setFunnelId(String(value))}
              options={onboardingOptions}
              required
              fullWidth
              disabled={busy || onboardingsLoading}
              loading={onboardingsLoading}
              loadingText="Loading onboardings..."
              noOptionsText="No onboardings found"
              placeholder="Select onboarding"
              data-testid="strategy-lift-funnel-id"
            />

            <StyledSelectDropdown
              label="Version"
              value={versionId}
              onChange={(value) => setVersionId(String(value))}
              options={versionOptions}
              required
              fullWidth
              disabled={busy || versionsLoading || !funnelId}
              loading={versionsLoading}
              loadingText="Loading versions..."
              noOptionsText="No versions found"
              placeholder="Current version"
              data-testid="strategy-lift-version-id"
            />

            <StyledTextField
              label="Target Strategy ID"
              value={targetStrategyId}
              onChange={setTargetStrategyId}
              fullWidth
              disabled={busy}
              placeholder="Leave empty to create a new strategy"
              data-testid="strategy-lift-target-strategy-id"
            />

            <StyledTextField
              label="Strategy Name"
              value={name}
              onChange={setName}
              fullWidth
              disabled={busy}
              placeholder="Strategy name"
              data-testid="strategy-lift-name"
            />

            <FormControlLabel
              control={
                <Switch
                  checked={replaceExisting}
                  onChange={(event) => setReplaceExisting(event.target.checked)}
                  disabled={busy}
                  inputProps={{ 'data-testid': 'strategy-lift-replace-existing' } as any}
                />
              }
              label="Replace existing strategy content"
            />
          </Stack>
        ) : (
          <Box data-testid="strategy-lift-plan">
            <Stack spacing={2}>
              <Typography variant="body2" color="text.secondary">
                Source: {funnelId} / {versionId}
              </Typography>

              <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                <Chip size="small" color="success" label={`${preview?.blockCount ?? previewBlocks.length} blocks`} />
                <Chip size="small" variant="outlined" label={`${preview?.screenCount ?? 0} screens`} />
                <Chip
                  size="small"
                  variant="outlined"
                  label={`${preview?.looseScreens?.length ?? 0} loose screens`}
                />
              </Stack>

              {previewBlocks.length > 0 && (
                <Stack spacing={1}>
                  <Typography sx={{ fontSize: '0.75rem', fontWeight: 600, letterSpacing: '0.4px', textTransform: 'uppercase', color: 'text.secondary' }}>
                    Blocks to create
                  </Typography>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell sx={HEAD_CELL_SX}>Block</TableCell>
                        <TableCell sx={HEAD_CELL_SX}>Screens</TableCell>
                        <TableCell sx={HEAD_CELL_SX}>Type</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {previewBlocks.map((block) => (
                        <TableRow key={block.entityId}>
                          <TableCell>
                            <Typography variant="body2" component="span">
                              {block.name || block.entityId}
                            </Typography>
                            <Typography variant="caption" color="text.secondary" component="span" sx={{ ml: 0.5 }}>
                              {block.entityId}
                            </Typography>
                          </TableCell>
                          <TableCell>{block.screenCount}</TableCell>
                          <TableCell>
                            <Typography variant="body2" color="text.secondary">
                              {block.blockType || '—'}
                            </Typography>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </Stack>
              )}

              <StrategySyncPlanView
                data={planData}
                direction="lift"
                error={previewError}
                showLooseSection
                availableBlocks={availableBlocks}
                resolutions={resolutions}
                onResolutionsChange={setResolutions}
              />
            </Stack>
          </Box>
        )}
      </DialogContent>

      <DialogFooter
        primaryAction={primaryAction}
        secondaryAction={secondaryAction}
        additionalActions={additionalActions}
      />
    </BaseDialog>
  );
};

StrategyLiftDialog.displayName = 'StrategyLiftDialog';
